/**
 * Skeleton presets (§6.2): each at the final size of the thing it stands in
 * for, so nothing moves when data arrives. Decorative (aria-hidden): the
 * region around a skeleton sets aria-busy and says what is loading.
 */
import { ArrowLeft01Icon, ArrowRight01Icon } from '@hugeicons/core-free-icons';
import type { CSSProperties } from 'react';
import { Icon } from '@/components/primitives/icon';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/** Varied bar widths, so rows read as text rather than a grid. */
const ROW_WIDTHS = ['72%', '56%', '84%', '64%', '78%', '50%', '90%', '60%'];

function px(value: number): CSSProperties {
  return { width: value };
}

// ---------------------------------------------------------------------------

export interface SkeletonKpiProps {
  /** Hide the sparkline row for tiles without one. Default true. */
  spark?: boolean;
  className?: string;
}

/** KPI tile: label 80 × 12, value 120 × 28, delta 140 × 12, sparkline full width × 24. */
export function SkeletonKpi({
  spark = true,
  className,
}: Readonly<SkeletonKpiProps>) {
  return (
    <div
      aria-hidden="true"
      data-slot="skeleton-kpi"
      className={cn('flex flex-col gap-2 p-4', className)}
    >
      <Skeleton className="h-3" style={px(80)} />
      <Skeleton className="h-7" style={px(120)} />
      <Skeleton className="h-3 max-w-full" style={px(140)} />
      {spark ? <Skeleton className="mt-1 h-6 w-full" /> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------

interface SkeletonColumn {
  /** Column width in px; leave out for a flexible column. */
  width?: number;
  /** Money and numbers align end. */
  align?: 'start' | 'end';
}

export interface SkeletonTableProps {
  /** A column count, or the table's columns (widths and alignment). */
  columns: number | readonly SkeletonColumn[];
  /** Default 8. */
  rows?: number;
  /** Compact rows are 32 px instead of 40. */
  density?: 'comfortable' | 'compact';
  /** Shows the pager, disabled. Default true. */
  pager?: boolean;
  className?: string;
}

/**
 * Table: the header row, 8 rows of column-width bars at 40 px, and the pager
 * shown but disabled.
 */
export function SkeletonTable({
  columns,
  rows = 8,
  density = 'comfortable',
  pager = true,
  className,
}: Readonly<SkeletonTableProps>) {
  const cols: readonly SkeletonColumn[] =
    typeof columns === 'number'
      ? Array.from({ length: Math.max(1, columns) }, () => ({}))
      : columns;
  const colKeys = cols.map((_, i) => `col-${i}`);
  const rowKeys = Array.from({ length: rows }, (_, r) => `row-${r}`);
  const rowHeight = density === 'compact' ? 'h-8' : 'h-10';

  return (
    <div
      aria-hidden="true"
      data-slot="skeleton-table"
      className={cn('flex w-full flex-col', className)}
    >
      <div className="overflow-hidden">
        <table className="w-full table-fixed border-collapse">
          <colgroup>
            {colKeys.map((key, i) => (
              <col
                key={key}
                style={cols[i]?.width ? { width: cols[i]?.width } : undefined}
              />
            ))}
          </colgroup>
          <thead>
            <tr className="h-9 border-b border-border bg-surface-2">
              {colKeys.map((key, i) => (
                <th key={key} className="px-3">
                  <div
                    className={cn(
                      'flex',
                      cols[i]?.align === 'end'
                        ? 'justify-end'
                        : 'justify-start',
                    )}
                  >
                    <Skeleton className="h-3 w-16 max-w-full" />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rowKeys.map((rowKey, r) => (
              <tr
                key={rowKey}
                className={cn(rowHeight, 'border-b border-border')}
              >
                {colKeys.map((key, c) => (
                  <td key={key} className="px-3">
                    <div
                      className={cn(
                        'flex',
                        cols[c]?.align === 'end'
                          ? 'justify-end'
                          : 'justify-start',
                      )}
                    >
                      <Skeleton
                        className="h-3"
                        style={{
                          width:
                            ROW_WIDTHS[(r + c * 3) % ROW_WIDTHS.length] ??
                            '60%',
                        }}
                      />
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pager ? <SkeletonPager /> : null}
    </div>
  );
}

function SkeletonPager() {
  return (
    <div className="flex h-11 items-center justify-between gap-3 px-3">
      <Skeleton className="h-3" style={px(140)} />
      <div className="flex items-center gap-1">
        <Button variant="outline" size="icon-sm" disabled tabIndex={-1}>
          <Icon icon={ArrowLeft01Icon} size={14} />
        </Button>
        <Button variant="outline" size="icon-sm" disabled tabIndex={-1}>
          <Icon icon={ArrowRight01Icon} size={14} />
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Record sheet (§6.1): hero bars (amount 160 × 28, title 240 × 16, meta
 * 320 × 12), two key-value groups and a 3-node timeline.
 */
export function SkeletonSheet({ className }: Readonly<{ className?: string }>) {
  return (
    <div
      aria-hidden="true"
      data-slot="skeleton-sheet"
      className={cn('flex flex-col gap-6 p-4', className)}
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <Skeleton className="h-7" style={px(160)} />
          <Skeleton className="h-6 w-20" />
        </div>
        <Skeleton className="h-4 max-w-full" style={px(240)} />
        <Skeleton className="h-3 max-w-full" style={px(320)} />
      </div>
      {[0, 1].map((group) => (
        <div key={group} className="flex flex-col gap-3">
          <Skeleton className="h-3 w-24" />
          <div className="grid grid-cols-2 gap-x-6 gap-y-3">
            {[0, 1, 2, 3].map((row) => (
              <div key={row} className="flex flex-col gap-1.5">
                <Skeleton className="h-3" style={px(80)} />
                <Skeleton
                  className="h-3.5"
                  style={{ width: ROW_WIDTHS[(group * 4 + row) % 8] }}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="flex flex-col gap-4">
        {[0, 1, 2].map((node) => (
          <div key={node} className="flex items-start gap-3">
            <Skeleton className="mt-0.5 size-2.5 rounded-full" />
            <div className="flex flex-1 flex-col gap-1.5">
              <Skeleton
                className="h-3.5"
                style={{ width: ROW_WIDTHS[node + 2] }}
              />
              <Skeleton className="h-3 w-24" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
